import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Badge } from '@neo/primitives/badge'
import { CircleCheck, CircleX } from 'lucide-react'
import { AnimatedGradientText } from '../../vibe-agent/components/animated-gradient-text'
import { useEvalStore } from '../store'
import type { EvalTestCase } from '../types'
import { TestCaseDetails } from './test-case-details'

interface TestCaseRowContextOverride {
  isPaused: boolean
  currentRunId: number | null
}

interface TestCaseRowProps {
  testCase: EvalTestCase
  context?: TestCaseRowContextOverride
}

export const TestCaseRow = ({ testCase, context }: TestCaseRowProps) => {
  const storeIsPaused = useEvalStore((state) => state.isPaused)
  const storeCurrentRunId = useEvalStore((state) => state.currentRunId)

  const isPaused = context ? context.isPaused : storeIsPaused
  const currentRunId = context ? context.currentRunId : storeCurrentRunId

  const isCancelled = isPaused || (currentRunId !== null && testCase.runId !== currentRunId)

  const hasResults = testCase.status === 'passed' || testCase.status === 'failed'

  const statusConfig = {
    pending: { color: 'text-muted-foreground', bg: 'bg-transparent', label: 'Pending' },
    testing: {
      color: isCancelled ? 'text-muted-foreground' : 'text-brand-text',
      bg: 'bg-transparent',
      label: isCancelled ? 'Simulation cancelled' : 'Simulating',
    },
    passed: { color: 'text-success-tag-foreground', bg: 'bg-success-tag', label: 'Pass' },
    failed: { color: 'text-danger-foreground', bg: 'bg-danger', label: 'Fail' },
  }[testCase.status]

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={testCase.id} className="border-none">
        <AccordionTrigger
          iconPosition="left"
          disabled={!hasResults}
          className={cn(
            'w-full flex items-center gap-2 justify-start py-2 hover:bg-bg2 hover:no-underline rounded-lg px-2 transition-colors',
          )}
          data-testid={`test-case-${testCase.id}`}
        >
          <span className="text-s-400 text-secondary text-left">{testCase.name}</span>
          <Badge
            className={cn(
              'text-xs-400 flex items-center gap-1 px-2 py-1 ml-auto text-right whitespace-nowrap',
              statusConfig.bg,
              statusConfig.color,
              `hover:${statusConfig.bg}`,
            )}
          >
            {testCase.status === 'passed' && <CircleCheck className="h-3 w-3 " aria-hidden />}
            {testCase.status === 'failed' && <CircleX className="h-3 w-3 " aria-hidden />}
            {testCase.status === 'testing' && !isCancelled ? (
              <AnimatedGradientText>{statusConfig.label}</AnimatedGradientText>
            ) : (
              statusConfig.label
            )}
          </Badge>
        </AccordionTrigger>
        <AccordionContent className="pb-4">
          <TestCaseDetails testCase={testCase} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
